import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/common/InfoTooltip';

interface KpiItem {
  label: string;
  value: number | string;
  tooltip?: string;
  variant?: 'default' | 'success' | 'danger' | 'warning';
  valueClassName?: string;
  suffix?: string;
  subtitle?: string;
  onClick?: () => void;
  active?: boolean;
}

interface KpiStripProps {
  items: KpiItem[];
  className?: string;
}

const variantClasses = {
  default: 'dls-text-foreground',
  success: 'dls-text-status-online',
  danger: 'dls-text-status-offline',
  warning: 'dls-text-status-warning',
};

export type { KpiItem };

export function KpiStrip({ items, className }: KpiStripProps) {
  return (
    <div className={cn('dls-grid dls-grid-cols-2 md:dls-flex dls-bg-neutral-contrast dls-rounded-lg', className)}>
      {items.map((item, i) => (
        <div
          key={item.label}
          className={cn(
            'dls-flex-1 dls-text-center dls-py-5 dls-px-4 sm:dls-px-6 dls-border-neutral-quarternary',
            // Desktop: right border between items
            i < items.length - 1 && 'md:dls-border-r',
            // Mobile 2-col grid: right border on left column, bottom border on non-last rows
            i % 2 === 0 && i < items.length - 1 && 'dls-border-r md:dls-border-r-0',
            Math.floor(i / 2) < Math.floor((items.length - 1) / 2) && 'dls-border-b md:dls-border-b-0',
            // Odd-count last item spans full width
            i === items.length - 1 && items.length % 2 !== 0 && 'dls-col-span-2 md:dls-col-span-1',
            item.onClick && 'dls-cursor-pointer hover:dls-bg-neutral-quarternary dls-transition-colors',
            item.active && 'dls-ring-2 dls-ring-primary dls-ring-inset'
          )}
          onClick={item.onClick}
        >
          <div className="dls-text-xs dls-font-semibold dls-text-muted-foreground dls-flex dls-items-center dls-justify-center dls-gap-1">
            {item.label}
            {item.tooltip && <InfoTooltip text={item.tooltip} />}
          </div>
          <div className={cn('dls-font-mono dls-text-2xl sm:dls-text-4xl dls-font-bold dls-mt-1', item.valueClassName || variantClasses[item.variant ?? 'default'])}>
            {item.value}
            {item.suffix && <span className="dls-text-lg dls-text-muted-foreground dls-ml-1 dls-font-sans">{item.suffix}</span>}
          </div>
          {item.subtitle && (
            <div className="dls-text-xs dls-text-muted-foreground dls-mt-1">{item.subtitle}</div>
          )}
        </div>
      ))}
    </div>
  );
}
